import React from 'react';
import { scrollIntoView as scrollElementIntoView } from './ScrollIntoView';

const EDGE_THRESHOLD_PX = 2;

const MOMENTUM_RECENT_INPUT_MS = 650;
const MOMENTUM_MIN_DELTA_PX = 0.35;
const MOMENTUM_MAX_DELTA_PX = 80;
const MOMENTUM_DECAY = 0.92;
const MOMENTUM_DECAY_AT_EDGE = 0.97;
const MOMENTUM_EDGE_COOLDOWN_MS = 60;
const MOMENTUM_MAX_EDGE_STALL_FRAMES = 30;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function now() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

class Viewport extends React.Component {
  containerRef = React.createRef();

  resizeObserver = null;
  mutationObserver = null;
  bottomLockFrame = null;

  /*
   * Kept for compatibility with your original VirtualizedView, which called:
   * viewport.registerScrollHandlers(...)
   *
   * The cleaner version can also pass onNearTop/onNearBottom as props.
   */
  onNearTopHandler = null;
  onNearBottomHandler = null;

  isBottomLock = Boolean(this.props.isBottomAligned);
  isNearTop = false;
  isNearBottom = false;

  lastScrollTop = 0;
  lastUserDeltaY = 0;
  lastUserDeltaYTime = 0;
  lastTouchY = null;

  scrollDisabled = false;
  releaseScrollDisabledFrame = null;

  viewAnchor = null;
  viewAnchorFrame = null;

  momentum = null;
  momentumFrame = null;
  lastEmittedBottomLock = null;

  lastEdgeEmitTime = {
    top: 0,
    bottom: 0,
  };

  registerScrollHandlers(onNearTop, onNearBottom) {
    this.onNearTopHandler = onNearTop;
    this.onNearBottomHandler = onNearBottom;
  }

  componentDidMount() {
    const container = this.containerRef.current;

    if (container) {
      this.lastScrollTop = container.scrollTop;
    }

    if (this.props.onCreate) {
      this.props.onCreate(this);
    }

    if (this.props.isBottomAligned) {
      window.requestAnimationFrame(() => this.scrollToBottom());
      this.startBottomLockObservers();
    } else {
      this.emitBottomLockChanged(true);
    }

    this.scheduleViewAnchorUpdate();
  }

  componentWillUnmount() {
    this.stopMomentum();

    if (this.releaseScrollDisabledFrame) {
      window.cancelAnimationFrame(this.releaseScrollDisabledFrame);
      this.releaseScrollDisabledFrame = null;
    }

    if (this.bottomLockFrame) {
      window.cancelAnimationFrame(this.bottomLockFrame);
      this.bottomLockFrame = null;
    }

    if (this.viewAnchorFrame) {
      window.cancelAnimationFrame(this.viewAnchorFrame);
      this.viewAnchorFrame = null;
    }

    this.stopBottomLockObservers();
  }

  startBottomLockObservers = () => {
    const container = this.containerRef.current;

    if (!container) {
      return;
    }

    this.stopBottomLockObservers();

    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(this.handleBottomLockLayoutChange);
      this.observeBottomLockResizeTargets();
    }

    if (typeof MutationObserver === 'function') {
      this.mutationObserver = new MutationObserver(() => {
        this.observeBottomLockResizeTargets();
        // ResizeObserver owns geometry changes. MutationObserver only keeps
        // its targets current; treating opacity/class mutations as layout
        // changes can restore a stale anchor while text is streaming.
        if (!this.resizeObserver) {
          this.handleBottomLockLayoutChange();
        }
      });

      this.mutationObserver.observe(container, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
  };

  stopBottomLockObservers = () => {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  };

  observeBottomLockResizeTargets = () => {
    const container = this.containerRef.current;

    if (!container || !this.resizeObserver) {
      return;
    }

    this.resizeObserver.disconnect();
    this.resizeObserver.observe(container);

    Array.from(container.children).forEach(child => {
      this.resizeObserver.observe(child);
    });
  };

  maintainBottomLock = () => {
    if (!this.props.isBottomAligned || !this.canBottomLock() || !this.isBottomLock) {
      return false;
    }

    return this.scrollToBottom();
  };

  handleBottomLockLayoutChange = () => {
    /*
     * Disclosure and Swiper auto-height updates can finish after React commits.
     * Pin before the next paint, then schedule one more pass for any dependent
     * layout that settles in the following animation frame.
     *
     * When the bottom lock is NOT active, the same layout changes must keep
     * the user's reading position stationary instead: restore the tracked
     * viewport anchor so content above the fold cannot shove the view around.
     */
    if (!this.maintainBottomLock()) {
      this.restoreObservedAnchor();
    }
    this.scheduleBottomLockScroll();
  };

  /*
   * The topmost visible item is the user's reading position. It is captured
   * continuously (on scroll and after every programmatic adjustment) so that
   * observer-driven layout changes — which fire after the DOM already moved —
   * still know where the view was.
   */
  updateViewAnchor = () => {
    const container = this.containerRef.current;
    const { getId } = this.props;
    const items = this.props.items || [];

    if (!container || !getId || !items.length) {
      this.viewAnchor = null;
      return;
    }

    for (const item of items) {
      const anchor = this.captureAnchor(getId(item));

      if (!anchor) {
        continue;
      }

      if (anchor.bottom <= 0) {
        continue;
      }

      this.viewAnchor = anchor;
      return;
    }

    this.viewAnchor = null;
  };

  scheduleViewAnchorUpdate = () => {
    if (this.viewAnchorFrame) {
      return;
    }

    this.viewAnchorFrame = window.requestAnimationFrame(() => {
      this.viewAnchorFrame = null;
      this.updateViewAnchor();
    });
  };

  updateViewAnchorAfterScroll = deltaY => {
    const container = this.containerRef.current;
    const anchor = this.viewAnchor;

    if (!container || !anchor || !Number.isFinite(deltaY)) {
      this.updateViewAnchor();
      return;
    }

    // Scrolling moves every rendered item by the inverse pixel delta. Keep
    // the saved geometry in the same coordinate space immediately so a
    // streaming update cannot restore the previous frame's reading position.
    anchor.top -= deltaY;
    anchor.bottom -= deltaY;

    if (anchor.bottom <= 0 || anchor.top >= container.clientHeight) {
      this.updateViewAnchor();
    }
  };

  restoreObservedAnchor = () => {
    if (this.isBottomLocked()) {
      return false;
    }

    const container = this.containerRef.current;
    const previous = this.viewAnchor;

    if (!container || !previous) {
      return false;
    }

    const current = this.captureAnchor(previous.id);

    if (!current) {
      this.scheduleViewAnchorUpdate();
      return false;
    }

    const delta = current.top - previous.top;

    if (Math.abs(delta) < 0.5) {
      return false;
    }

    this.runWithoutScrollHandling(() => {
      container.scrollTop += delta;
    });

    return true;
  };

  scheduleBottomLockScroll = () => {
    if (!this.props.isBottomAligned || !this.canBottomLock() || !this.isBottomLock) {
      return;
    }

    if (this.bottomLockFrame) {
      return;
    }

    this.bottomLockFrame = window.requestAnimationFrame(() => {
      this.bottomLockFrame = null;

      this.maintainBottomLock();
    });
  };

  getSnapshotBeforeUpdate(prevProps) {
    const container = this.containerRef.current;

    if (!container) {
      return null;
    }

    const prevItems = prevProps.items || [];

    return {
      anchors: this.getAnchors(prevItems),
      scrollTop: container.scrollTop,
      scrollHeight: container.scrollHeight,
      clientHeight: container.clientHeight,
      wasBottomLocked: this.isBottomLocked(prevProps),

      itemsChanged: prevProps.items !== this.props.items,
      firstItemId: prevItems.length ? this.props.getId(prevItems[0]) : null,

      recentDeltaY: this.lastUserDeltaY,
      recentDeltaAge: now() - this.lastUserDeltaYTime,

      hadMomentum: Boolean(this.momentum),
      momentumDeltaY: this.momentum ? this.momentum.deltaY : this.lastUserDeltaY,
    };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const container = this.containerRef.current;

    if (!container) {
      return;
    }

    if (prevProps.isBottomAligned !== this.props.isBottomAligned) {
      if (this.props.isBottomAligned) {
        this.isBottomLock = this.isAtBottom();
        this.startBottomLockObservers();
      } else {
        this.stopBottomLockObservers();
        this.isBottomLock = false;
        this.emitBottomLockChanged();
      }
    }

    if (!snapshot) {
      this.observeBottomLockResizeTargets();
      this.emitBottomLockChanged();
      return;
    }

    const heightChanged = container.scrollHeight !== snapshot.scrollHeight;
    const shouldPinToBottom =
      this.props.isBottomAligned && this.canBottomLock() && snapshot.wasBottomLocked;

    if (shouldPinToBottom) {
      this.scrollToBottom();
    } else {
      const restoredAnchor = this.restoreScrollAnchor(snapshot);
      const items = this.props.items || [];
      const firstItemId = items.length ? this.props.getId(items[0]) : null;

      this.maybeResumeMomentumAfterRender(snapshot, {
        restoredAnchor,
        heightChanged,
        windowSwapped: firstItemId !== snapshot.firstItemId,
      });
    }

    this.observeBottomLockResizeTargets();
    this.emitBottomLockChanged();
    this.scheduleViewAnchorUpdate();
  }

  getContentRef = () => {
    return this.containerRef.current;
  };

  getNearTopHandler = () => {
    return this.props.onNearTop || this.onNearTopHandler;
  };

  getNearBottomHandler = () => {
    return this.props.onNearBottom || this.onNearBottomHandler;
  };

  getMaxScrollTop = () => {
    const container = this.containerRef.current;

    if (!container) {
      return 0;
    }

    return Math.max(0, container.scrollHeight - container.clientHeight);
  };

  isAtTop = () => {
    const container = this.containerRef.current;
    return Boolean(container && container.scrollTop <= EDGE_THRESHOLD_PX);
  };

  isAtBottom = () => {
    const container = this.containerRef.current;

    if (!container) {
      return false;
    }

    const scrollBottom =
      container.scrollHeight - container.clientHeight - container.scrollTop;

    return scrollBottom <= EDGE_THRESHOLD_PX;
  };

  canBottomLock = (props = this.props) => props.canBottomLock !== false;

  isBottomLocked = (props = this.props) => {
    /*
     * canBottomLock describes whether the current virtual window contains the
     * list end. It can be false for one render while that window advances, but
     * that transition is not a user request to release the lock.
     */
    return Boolean(
      this.isBottomLock || (this.canBottomLock(props) && this.isAtBottom())
    );
  };

  emitBottomLockChanged = (force = false) => {
    const isBottomLock = this.isBottomLocked();

    if (!force && this.lastEmittedBottomLock === isBottomLock) {
      return;
    }

    this.lastEmittedBottomLock = isBottomLock;

    if (this.props.onBottomLockChanged) {
      this.props.onBottomLockChanged(isBottomLock);
    }
  };

  runWithoutScrollHandling = fn => {
    if (this.releaseScrollDisabledFrame) {
      window.cancelAnimationFrame(this.releaseScrollDisabledFrame);
      this.releaseScrollDisabledFrame = null;
    }

    this.scrollDisabled = true;

    try {
      fn();
    } finally {
      const container = this.containerRef.current;

      if (container) {
        this.lastScrollTop = container.scrollTop;
      }

      /*
       * Scroll events from programmatic scrollTop writes may fire async.
       * Keep disabled until the next frame so those events do not look like
       * real user/native momentum scroll events.
       */
      this.releaseScrollDisabledFrame = window.requestAnimationFrame(() => {
        this.scrollDisabled = false;
        this.releaseScrollDisabledFrame = null;
      });
    }
  };

  findItemElement = id => {
    const container = this.containerRef.current;

    if (!container) {
      return null;
    }

    try {
      return container.querySelector(this.props.getSelector(id));
    } catch (error) {
      if (this.props.debug) {
        console.warn('[Viewport] Invalid selector for item:', id, error);
      }

      return null;
    }
  };

  captureAnchor = id => {
    const container = this.containerRef.current;

    if (!container || id == null) {
      return null;
    }

    const element = this.findItemElement(id);

    if (!element) {
      return null;
    }

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    return {
      id,
      top: elementRect.top - containerRect.top,
      bottom: elementRect.bottom - containerRect.top,
      distanceFromTop: Math.abs(elementRect.top - containerRect.top),
      distanceFromBottom: Math.abs(elementRect.bottom - containerRect.bottom),
    };
  };

  getAnchors = items => {
    if (!items.length) {
      return [];
    }

    const { getId } = this.props;

    let nearestTop = null;
    let nearestBottom = null;

    for (const item of items) {
      const anchor = this.captureAnchor(getId(item));

      if (!anchor) {
        continue;
      }

      if (!nearestTop || anchor.distanceFromTop < nearestTop.distanceFromTop) {
        nearestTop = anchor;
      }

      if (
        !nearestBottom ||
        anchor.distanceFromBottom < nearestBottom.distanceFromBottom
      ) {
        nearestBottom = anchor;
      }
    }

    return [nearestTop, nearestBottom].filter(Boolean);
  };

  restoreScrollAnchor = snapshot => {
    const container = this.containerRef.current;

    if (!container || !snapshot.anchors || snapshot.anchors.length === 0) {
      return false;
    }

    for (const previousAnchor of snapshot.anchors) {
      const currentAnchor = this.captureAnchor(previousAnchor.id);

      if (!currentAnchor) {
        continue;
      }

      const deltaTop = currentAnchor.top - previousAnchor.top;

      if (Math.abs(deltaTop) < 0.5) {
        return false;
      }

      this.runWithoutScrollHandling(() => {
        container.scrollTop += deltaTop
      });
      return true;
    }

    return false;
  };

  removeBottomLock = () => {
    this.isBottomLock = false;
    this.emitBottomLockChanged();
  };

  serialNum = 0
  scrollIntoView = async (element, options, wasCancelled) => {
    const container = this.containerRef.current;

    if (!container || !element) {
      return false;
    }

    this.stopMomentum();
    this.scrollDisabled = true;

    const serialNum = ++this.serialNum
    const wasCancelledLocally = () => {
      return wasCancelled() ||
        this.serialNum !== serialNum
    }

    try {
      const didFinish = await scrollElementIntoView(
        element,
        options,
        wasCancelledLocally,
        this.props.debug,
      );

      return didFinish;
    } finally {
      this.lastScrollTop = container.scrollTop;
      this.scrollDisabled = false;
    }
  };

  scrollToTop = () => {
    const container = this.containerRef.current;

    if (!container) {
      return false;
    }

    this.stopMomentum();

    this.runWithoutScrollHandling(() => {
      container.scrollTop = 0;
    });

    this.isBottomLock = false;
    this.isNearTop = false;
    this.isNearBottom = false;
    this.emitBottomLockChanged();

    return true;
  };

  scrollToBottom = () => {
    const container = this.containerRef.current;

    if (!container) {
      return false;
    }

    this.stopMomentum();

    this.runWithoutScrollHandling(() => {
      container.scrollTop = this.getMaxScrollTop();
    });

    this.isBottomLock = true;
    this.isNearTop = false;
    this.isNearBottom = true
    this.emitBottomLockChanged();

    return true;
  };

  setScrollTop = scrollTop => {
    const container = this.containerRef.current;

    if (!container) {
      return false;
    }

    this.stopMomentum();

    this.runWithoutScrollHandling(() => {
      container.scrollTop = scrollTop
    });

    return true;
  };

  recordUserDelta = (deltaY, source = 'user') => {
    if (!Number.isFinite(deltaY) || deltaY === 0) {
      return;
    }

    /*
     * Real user input takes precedence over synthetic carry-over.
     */
    if (source === 'user') {
      this.stopMomentum();
    }

    this.lastUserDeltaY = deltaY;
    this.lastUserDeltaYTime = now();

    if (deltaY < 0) {
      this.isBottomLock = false;
    } else if (deltaY > 0 && this.canBottomLock() && this.isAtBottom()) {
      this.isBottomLock = true;
    }

    this.emitBottomLockChanged();
  };

  emitNearTop = (nearTop, force = false) => {
    if (!nearTop) {
      this.isNearTop = false;
      return;
    }

    if (this.isNearTop && !force) {
      return;
    }

    if (force) {
      const timestamp = now();

      if (timestamp - this.lastEdgeEmitTime.top < MOMENTUM_EDGE_COOLDOWN_MS) {
        return;
      }

      this.lastEdgeEmitTime.top = timestamp;
    }

    this.isNearTop = true;

    const handler = this.getNearTopHandler();

    if (handler) {
      handler();
    }
  };

  emitNearBottom = (nearBottom, force = false) => {
    if (!nearBottom) {
      this.isNearBottom = false;
      return;
    }

    if (this.isNearBottom && !force) {
      return;
    }

    if (force) {
      const timestamp = now();

      if (timestamp - this.lastEdgeEmitTime.bottom < MOMENTUM_EDGE_COOLDOWN_MS) {
        return;
      }

      this.lastEdgeEmitTime.bottom = timestamp;
    }

    this.isNearBottom = true;

    const handler = this.getNearBottomHandler();

    if (handler) {
      handler();
    }
  };

  checkEdges = (deltaY, force = false) => {
    if (!Number.isFinite(deltaY) || deltaY === 0) {
      return;
    }

    if (deltaY < 0) {
      this.emitNearTop(this.isAtTop(), force);
      this.emitNearBottom(false);
    } else {
      this.emitNearBottom(this.isAtBottom(), force);
      this.emitNearTop(false);
    }
  };

  handleWheel = event => {
    this.recordUserDelta(event.deltaY, 'user');
    this.checkEdges(event.deltaY, true);
  };

  handleTouchStart = event => {
    this.stopMomentum();

    if (event.touches.length > 0) {
      this.lastTouchY = event.touches[0].clientY;
    }
  };

  handleTouchMove = event => {
    if (event.touches.length === 0) {
      return;
    }

    const currentY = event.touches[0].clientY;

    if (this.lastTouchY == null) {
      this.lastTouchY = currentY;
      return;
    }

    /*
     * Positive delta means the user is moving down the list.
     * Negative delta means the user is moving up the list.
     */
    const deltaY = this.lastTouchY - currentY;
    this.lastTouchY = currentY;

    this.recordUserDelta(deltaY, 'user');
    this.checkEdges(deltaY, true);
  };

  handleScroll = () => {
    const container = this.containerRef.current;

    if (!container) {
      return;
    }

    const scrollTop = container.scrollTop;

    if (this.scrollDisabled) {
      this.lastScrollTop = scrollTop;
      return;
    }

    const nativeDeltaY = scrollTop - this.lastScrollTop;
    const effectiveDeltaY = nativeDeltaY || this.lastUserDeltaY;

    this.lastScrollTop = scrollTop;
    this.updateViewAnchorAfterScroll(nativeDeltaY);

    /*
     * This catches native momentum scroll events that happen without wheel/touch
     * events. Do not mark them as source='user', because they should not cancel
     * a synthetic carry-over unless an actual new gesture happens.
     */
    this.recordUserDelta(effectiveDeltaY, 'native');
    this.checkEdges(effectiveDeltaY);

    if (this.props.onScroll) {
      this.props.onScroll(scrollTop);
    }

    this.scheduleViewAnchorUpdate();
  };

  shouldResumeMomentumAfterRender = (snapshot, details) => {
    if (this.props.preserveMomentum === false) {
      return false;
    }

    /*
     * Momentum continues across a render only when that render plausibly
     * interrupted the user's motion: either a glide we were already running,
     * or a virtual window swap (the rendered slice moved) right after real
     * input. Renders caused by content growth elsewhere — e.g. a message
     * streaming below the fold — must never resurrect a finished gesture,
     * or the view sails away from where the user stopped.
     */
    const renderMayHaveInterruptedMomentum =
      snapshot.hadMomentum ||
      (details.windowSwapped &&
        snapshot.recentDeltaAge <= MOMENTUM_RECENT_INPUT_MS);

    if (!renderMayHaveInterruptedMomentum) {
      return false;
    }

    const deltaY = snapshot.hadMomentum
      ? snapshot.momentumDeltaY
      : snapshot.recentDeltaY;

    return Number.isFinite(deltaY) && Math.abs(deltaY) >= MOMENTUM_MIN_DELTA_PX;
  };

  maybeResumeMomentumAfterRender = (snapshot, details) => {
    if (!this.shouldResumeMomentumAfterRender(snapshot, details)) {
      return;
    }

    const deltaY = snapshot.hadMomentum
      ? snapshot.momentumDeltaY
      : snapshot.recentDeltaY;

    this.startMomentum(deltaY);
  };

  startMomentum = initialDeltaY => {
    const deltaY = clamp(
      Number(initialDeltaY) || 0,
      -MOMENTUM_MAX_DELTA_PX,
      MOMENTUM_MAX_DELTA_PX,
    );

    if (Math.abs(deltaY) < MOMENTUM_MIN_DELTA_PX) {
      return;
    }

    this.stopMomentum();

    this.momentum = {
      deltaY,
      edgeStallFrames: 0,
    };

    this.scheduleMomentumFrame();
  };

  stopMomentum = () => {
    if (this.momentumFrame) {
      window.cancelAnimationFrame(this.momentumFrame);
      this.momentumFrame = null;
    }

    this.momentum = null;
  };

  scheduleMomentumFrame = () => {
    if (!this.momentum) {
      return;
    }

    if (this.momentumFrame) {
      window.cancelAnimationFrame(this.momentumFrame);
    }

    this.momentumFrame = window.requestAnimationFrame(this.runMomentumFrame);
  };

  runMomentumFrame = () => {
    this.momentumFrame = null;

    const momentum = this.momentum;
    const container = this.containerRef.current;

    if (!momentum || !container) {
      this.stopMomentum();
      return;
    }

    const deltaY = momentum.deltaY;

    if (Math.abs(deltaY) < MOMENTUM_MIN_DELTA_PX) {
      this.stopMomentum();
      return;
    }

    const before = container.scrollTop;
    const maxScrollTop = this.getMaxScrollTop();
    const nextScrollTop = before + deltaY

    this.runWithoutScrollHandling(() => {
      container.scrollTop = nextScrollTop;
    });

    const after = container.scrollTop;
    const actualDeltaY = after - before;

    this.lastScrollTop = after;

    /*
     * Synthetic momentum is treated as a continuation of the user's previous
     * intent. It should be able to trigger window advancement at the edges.
     */
    this.recordUserDelta(deltaY, 'momentum');

    if (this.props.onScroll) {
      this.props.onScroll(after);
    }

    const blockedAtEdge =
      Math.abs(actualDeltaY) < 0.5 &&
      ((deltaY < 0 && this.isAtTop()) || (deltaY > 0 && this.isAtBottom()));

    this.checkEdges(deltaY, blockedAtEdge);

    if (blockedAtEdge) {
      /*
       * We are at the edge of the current rendered slice. Give VirtualizedView
       * a few frames to swap the window / load more. componentDidUpdate will
       * restart this momentum with the remaining delta after the DOM changes.
       */
      momentum.edgeStallFrames += 1;
      momentum.deltaY *= MOMENTUM_DECAY_AT_EDGE;

      if (momentum.edgeStallFrames > MOMENTUM_MAX_EDGE_STALL_FRAMES) {
        this.stopMomentum();
        return;
      }
    } else {
      momentum.edgeStallFrames = 0;
      momentum.deltaY *= MOMENTUM_DECAY;
    }

    this.scheduleMomentumFrame();
  };

  render() {
    const {
      items = [],
      renderItems,
      onKeyDown,
      autoFocus,
    } = this.props;

    return (
      <div
        ref={this.containerRef}
        onScroll={this.handleScroll}
        onWheel={this.handleWheel}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
        tabIndex={onKeyDown ? 0 : undefined}
        className="itemScroller"
      >
        {renderItems(items)}
      </div>
    );
  }
}

export default Viewport;
